import React, { useState, useEffect } from "react";
import Alert from "@mui/material/Alert";
import "./updatePassword.css";
import SignupToolbar from "../../components/lawyerToolbar/signupToolbar";
import Footer from "../../components/footer";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import PasswordField from "../../components/input/passwordField";
import { useDispatch } from "react-redux";
import { updateStatus } from "../../redux/attorneyDataSlice";

const UpdatePassword = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { search, hash } = useLocation();

  // pull the recovery token and type out of the query string
  useEffect(() => {
    const recoverSession = async () => {
      // 1) try the built-in v2 helper
      if (typeof supabase.auth.getSessionFromUrl === "function") {
        try {
          await supabase.auth.getSessionFromUrl({ storeSession: true });
          return; // done
        } catch (err) {
          console.warn("getSessionFromUrl failed, falling back:", err);
        }
      }

      // 2) manual fallback: look in both query and hash
      const allParams = new URLSearchParams(
        search.replace(/^\?/, "") + "&" + hash.replace(/^#/, "")
      );
      const access_token = allParams.get("access_token");
      const refresh_token = allParams.get("refresh_token");

      if (access_token && refresh_token) {
        try {
          await supabase.auth.setSession({ access_token, refresh_token });
        } catch (err) {
          console.error("Manual setSession failed:", err);
        }
      }
    };

    recoverSession();
  }, [search, hash]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [criteria, setCriteria] = useState({
    length: false,
    letter: false,
    number: false,
    special: false,
  });
  const [error, setError] = useState("");
  const [alert, setAlert] = useState(null);

  // run complexity checks on every change
  useEffect(() => {
    setCriteria({
      length: newPassword.length >= 8,
      letter: /[A-Za-z]/.test(newPassword),
      number: /\d/.test(newPassword),
      special: /[^A-Za-z0-9]/.test(newPassword),
    });
  }, [newPassword]);

  const isPasswordValid = Object.values(criteria).every(Boolean);
  const passwordsMatch = newPassword && newPassword === confirmPassword;
  const canSubmit = isPasswordValid && passwordsMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) {
      setError(
        !isPasswordValid
          ? "Password doesn't meet all complexity requirements."
          : "Passwords do not match."
      );
      return;
    }

    const { data, error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updateError) {
      setError(updateError.message);
    } else {
      dispatch(updateStatus("PASSWORD_RESET_SUCCESS"));
      setAlert({ severity: "success", message: "Password updated!" });
      // give a moment for user to see the banner
      setTimeout(() => {
        navigate("/grow/redirect");
      }, 1500);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <SignupToolbar />

      {alert && (
        <Alert severity={alert.severity} style={{ margin: "20px 30px 0" }}>
          {alert.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" style={{ margin: "20px 30px 0" }}>
          {error}
        </Alert>
      )}

      <div className="update-password-container">
        <form
          className="update-password-form-wrapper"
          onSubmit={handleSubmit}
          noValidate
        >
          <h2 className="update-password-heading">Create a new password</h2>
          <h3 className="update-password-subtitle">
            Please choose a strong new password
          </h3>

          <PasswordField
            title="New Password"
            placeholder="Enter your new password"
            isRequired
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />

          <ul className="password-criteria">
            <li className={criteria.length ? "valid" : "invalid"}>
              • At least 8 characters
            </li>
            <li className={criteria.letter ? "valid" : "invalid"}>
              • A letter (A–Z or a–z)
            </li>
            <li className={criteria.number ? "valid" : "invalid"}>
              • A number (0–9)
            </li>
            <li className={criteria.special ? "valid" : "invalid"}>
              • A special character (e.g. @#$%*)
            </li>
          </ul>

          <PasswordField
            title="Confirm Password"
            placeholder="Re‑enter your new password"
            isRequired
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <button
            type="submit"
            className="update-password-button"
            disabled={!canSubmit}
          >
            Continue
          </button>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default UpdatePassword;
